export async function formatResponse(response) {
  const data = await response.json();
  return { ok: response.ok, data };
}

export function ordinal_suffix_of(i) {
  const j = i % 10;
  const k = i % 100;
  if (j == 1 && k != 11) {
    return i + 'st';
  }
  if (j == 2 && k != 12) {
    return i + 'nd';
  }
  if (j == 3 && k != 13) {
    return i + 'rd';
  }
  return i + 'th';
}

export function preciseRound(value, precision) {
  const multiplier = Math.pow(10, precision || 0);
  return Math.round(value * multiplier) / multiplier;
}

export function oneDecimalRound(value, precision) {
  const multiplier = Math.pow(10, precision || 1);
  return Math.round(value * multiplier) / multiplier;
}

export function randomInteger(min, max) {
  if (!min || !max) {
    return Math.floor(Math.random());
  }
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function redGreenScale(integerValue, min, max) {
  const value = integerValue / (max - min);
  const hue = ((value) * 120).toString(10);
  return `hsl(${hue}, 80%, 50%)`;
}

export function averagePropertiesInArray(arrayOfObjects, propertyToAverage) {
  let count = 0;
  const average = arrayOfObjects.reduce((total, object) => {
    total += object[propertyToAverage];
    count++;
    return total;
  }, 0) / count;
  return average;
}


export function getCSRF() {
  const elem = document.querySelector('[name="csrfmiddlewaretoken"]');
  return elem?.value;
}


export function getHeaders() {
  return {
    'Content-Type': 'application/json',
    'X-CSRFToken': getCSRF(),
  };
}

export function prettifyMixedCase(sourceText) {
  if (!sourceText) {
    return;
  }

  let text = sourceText;

  if (typeof sourceText !== 'String') {
    text = sourceText.toString();
  }

  // Split on both underscores and dots
  const words = text.split(/[_.]/);

  // Capitalize each word
  const formatted = words.map((word) => {
    return word.charAt(0).toUpperCase() + word.slice(1);
  });

  const joined = formatted.join(' ');

  // Transforms PascalCase into capitalized words with spaces
  return joined.replace(/([a-z])([A-Z])|([A-Z]+)([A-Z][a-z])/g, '$1$3 $2$4');
}

export function cleanFileName(fileName) {
  if (!fileName) {
    return '—';
  }

  // Use regex to match and remove the date prefix (if present)
  const match = fileName.match(/^\d{4}-\d{2}-\d{2}_\d{6}_(.+)$/);
  if (match) {
    return match[1]; // Return the cleaned file name
  }
  // If no date prefix, return the original file name
  return fileName;
}

export function getValue(obj, keys, arrayIndex) {
  let current = obj;

  for (let i = 0; i < keys.length; i++) {
    if (current === undefined || current === null) {
      return undefined;
    }

    const key = keys[i];

    // Check if we're at an array in the path
    if (Array.isArray(current)) {
      // If we have items in the array, access by index
      if (current.length > arrayIndex) {
        current = current[arrayIndex][key];
      } else {
        return undefined;
      }
    } else {
      // Regular object property access
      current = current[key];
    }
  }

  return current;
}

export function formatDate(date) {
  if (!date) return 'N/A';
  const options = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };
  return new Date(date).toLocaleString('en-US', options);
}


export function formatShortDate(date) {
  if (!date) return 'N/A';
  const options = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  return new Date(date).toLocaleString('en-US', options);
}

const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

export function relativeDate(date) {
  const now = new Date();
  const target = new Date(date);
  const diff = target - now; // positive = future, negative = past

  const minutes = diff / 60000;
  const hours = minutes / 60;
  const days = hours / 24;
  const months = days / 30; // approx
  const years = days / 365; // approx

  if (Math.abs(minutes) < 60) {
    return capitalize(formatter.format(Math.round(minutes), 'minute'));
  } else if (Math.abs(hours) < 24) {
    return capitalize(formatter.format(Math.round(hours), 'hour'));
  } else if (Math.abs(days) < 30) {
    return capitalize(formatter.format(Math.round(days), 'day'));
  } else if (Math.abs(months) < 12) {
    return capitalize(formatter.format(Math.round(months), 'month'));
  } else {
    return capitalize(formatter.format(Math.round(years), 'year'));
  }
}

export function isPlainObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Debounce a function so it only runs after a delay since the last call.
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The delay in milliseconds.
 * @return {Function}
 */
export function debounce(func, wait) {
  let timeout;
  return function(...args) {
    const later = () => {
      timeout = null;
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function formatCurrency(value) {
  if (value === undefined || value === null) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function toCurrency(value) {
  // If it's a string check the format
  if (typeof value === 'string') {
    // If it's already formatted just return it
    if (value.startsWith('$')) return value;

    // If it's not the right format, try to parse a float.
    value = parseFloat(value);
    if (isNaN(value)) return '';
  }

  const dollars = value?.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return dollars ? `$${dollars}` : '';
};

export function objectIsEmpty(obj) {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
}

// null, undefined, '' (empty string), [] (empty array) and {} (empty object)
// are considered empty
// 0 and true / false
// are not
export function isEmptyValue(val) {
  if (val === null || val === undefined) return true;
  if (typeof val === 'string' && val.trim() === '') return true;
  if (Array.isArray(val) && val.length === 0) return true;
  if (
    typeof val === 'object' &&
    !Array.isArray(val) &&
    Object.keys(val).length === 0
  ) {
    return true;
  }
  return false;
}

export const isEmptyObject = (obj) => {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
};

export function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function getCurrentDateUSFormat() {
  const today = new Date();
  const mm = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const dd = String(today.getDate()).padStart(2, '0');
  const yyyy = today.getFullYear();

  return `${mm}-${dd}-${yyyy}`;
};

export function completionColor(percentage) {
  if (!percentage) return '#666';
  if (percentage > 90) return '#36B60C';
  else if (percentage > 50) return '#97b60c';
  else if (percentage > 20) return '#E68C0F';
  return '#E6210F';
};
